import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IonButton, IonContent, IonPage, IonSpinner } from '@ionic/react';
import {
  CheckAgeSignalsResult,
  InsightAgeCheck,
  ParentalControlType,
  RegulatoryFeature,
  RuntimeEnvironmentResult,
  SignificantUpdateChoice,
  UserStatus,
} from 'insight-age-check';

import './LaunchAgeGate.css';

import { AGE_ASSURANCE_DEBUG_ENABLED, ENVIRONMENT } from '../ts/environment';
import {
  defaultAgeGates,
  getAdultAcknowledgmentUnavailableMessage,
  getAgeInformationUnavailableMessage,
  getAgeSignalDecision,
  getSignificantUpdateApprovalDeniedMessage,
  getSignificantUpdateApprovalPendingMessage,
  isSignificantUpdateApprovalStatus,
} from '../ts/ageSignalBehavior';
import {
  AgeGateStartupDebugMode,
  getAgeGateStartupDebugResult,
  getStoredAgeGateStartupDebugMode,
} from '../ts/ageGateDebug';
import {
  AgeAssuranceTelemetryEvent,
  createAgeAssuranceTelemetryRunId,
  flushQueuedAgeAssuranceTelemetry,
  getAgeAssuranceSignalFields,
  getRuntimeEnvironmentTelemetryPayload,
  trackAgeAssuranceTelemetry,
} from '../ts/ageAssuranceTelemetry';
import {
  getIosSignificantUpdateDescription,
  hasStoredIosAdultAcknowledgment,
  iosSignificantUpdateConfig,
  isIosSignificantUpdateEnabled,
  setStoredIosAdultAcknowledgment,
} from '../ts/iosAgeAssurance';

interface LaunchAgeGateProps {
  appName: string;
  platform: string;
  uuid: string;
  osVersion?: string;
  onAllow: () => Promise<void>;
  onBlock: (message: string) => void;
  autoRunRealCheck?: boolean;
}

const RETRY_DELAY_MS = 1250;
const OFFLINE_NOTICE_DELAY_MS = 10000;
const CONNECTION_RETRY_DELAY_MS = 3000;
const offlineConnectionMessage = "It appears you've lost your internet connection. Please re-establish your connection and try again.";

const delay = (durationMs: number) => new Promise(resolve => {
  window.setTimeout(resolve, durationMs);
});

const hasNetworkConnection = () => (
  typeof window === 'undefined'
  || typeof window.navigator === 'undefined'
  || window.navigator.onLine !== false
);

const toLoggableError = (error: unknown) => {
  if (error instanceof Error) {
    const details = error as Error & { code?: string; errorMessage?: string };

    return {
      name: details.name,
      message: details.message,
      code: details.code ?? null,
      errorMessage: details.errorMessage ?? null,
    };
  }

  return {
    value: error,
  };
};

const getAgeAssuranceErrorCode = (error: unknown) => {
  const loggableError = toLoggableError(error) as { code?: string | null };

  return loggableError.code ?? null;
};

const isApiNotAvailableError = (error: unknown) => (
  getAgeAssuranceErrorCode(error) === 'API_NOT_AVAILABLE'
);

const hasRegulatoryFeature = (
  features: RegulatoryFeature[],
  target: RegulatoryFeature,
) => features.includes(target);

const hasParentalControl = (
  ageSignalResult: CheckAgeSignalsResult,
  target: ParentalControlType,
) => Boolean(ageSignalResult.activeParentalControls?.includes(target));

function LaunchAgeGate({
  appName,
  platform,
  uuid,
  osVersion,
  onAllow,
  onBlock,
  autoRunRealCheck = false,
}: LaunchAgeGateProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const [offlineNoticeVisible, setOfflineNoticeVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const autoCheckStartedRef = useRef(false);
  const launchRunIdRef = useRef(createAgeAssuranceTelemetryRunId());
  const isMountedRef = useRef(true);
  const isNativeAuto = platform !== 'web' && autoRunRealCheck;
  const logPrefix = '[AgeGate]';

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!isProcessing) {
      setOfflineNoticeVisible(false);
      return undefined;
    }

    let offlineNoticeTimer: number | undefined;

    const scheduleOfflineNotice = () => {
      if (offlineNoticeTimer !== undefined) {
        window.clearTimeout(offlineNoticeTimer);
      }

      if (hasNetworkConnection()) {
        setOfflineNoticeVisible(false);
        return;
      }

      offlineNoticeTimer = window.setTimeout(() => {
        if (isMountedRef.current && !hasNetworkConnection()) {
          setOfflineNoticeVisible(true);
        }
      }, OFFLINE_NOTICE_DELAY_MS);
    };

    scheduleOfflineNotice();
    window.addEventListener('online', scheduleOfflineNotice);
    window.addEventListener('offline', scheduleOfflineNotice);

    return () => {
      if (offlineNoticeTimer !== undefined) {
        window.clearTimeout(offlineNoticeTimer);
      }

      window.removeEventListener('online', scheduleOfflineNotice);
      window.removeEventListener('offline', scheduleOfflineNotice);
    };
  }, [isProcessing]);

  const trackTelemetryEvent = useCallback((
    event: Omit<AgeAssuranceTelemetryEvent, 'app_name' | 'app_environment' | 'launch_run_id' | 'os_version' | 'platform' | 'occurred_at'>,
  ) => {
    if (!uuid) {
      return;
    }

    void trackAgeAssuranceTelemetry(uuid, {
      ...event,
      app_name: appName,
      app_environment: ENVIRONMENT,
      launch_run_id: launchRunIdRef.current,
      os_version: osVersion,
      platform,
    });
  }, [appName, osVersion, platform, uuid]);

  const getErrorTelemetryFields = useCallback((error: unknown) => {
    const loggableError = toLoggableError(error) as {
      code?: string | null;
      errorMessage?: string | null;
      message?: string | null;
      value?: unknown;
    };

    return {
      api_code: loggableError.code ?? null,
      api_message: loggableError.errorMessage
        ?? loggableError.message
        ?? (typeof loggableError.value === 'string' ? loggableError.value : null),
      raw_payload: {
        error: loggableError,
      },
    };
  }, []);

  const trackApiError = useCallback((
    apiName: string,
    error: unknown,
    context: {
      behavior: string;
      elapsedMs: number;
      allowed?: boolean | null;
      source?: string;
      ageSignalResult?: CheckAgeSignalsResult | null;
      requiredRegulatoryFeatures?: RegulatoryFeature[];
      extraRawPayload?: Record<string, unknown>;
    },
  ) => {
    const errorFields = getErrorTelemetryFields(error);

    trackTelemetryEvent({
      event_type: 'api_error',
      allowed: context.allowed ?? null,
      behavior: context.behavior,
      source: context.source ?? apiName,
      required_features: context.requiredRegulatoryFeatures ?? [],
      api_name: apiName,
      api_code: errorFields.api_code,
      api_message: errorFields.api_message,
      elapsed_ms: context.elapsedMs,
      ...getAgeAssuranceSignalFields(context.ageSignalResult),
      raw_payload: {
        ...errorFields.raw_payload,
        ...context.extraRawPayload,
      },
    });
  }, [getErrorTelemetryFields, trackTelemetryEvent]);

  const waitForNetworkConnection = useCallback(async (reason: string) => {
    if (hasNetworkConnection()) {
      if (isMountedRef.current) {
        setOfflineNoticeVisible(false);
      }

      return false;
    }

    console.warn(`${logPrefix} waiting for network connection before continuing age verification`, {
      platform,
      reason,
      retryDelayMs: CONNECTION_RETRY_DELAY_MS,
      noticeDelayMs: OFFLINE_NOTICE_DELAY_MS,
    });

    while (isMountedRef.current && !hasNetworkConnection()) {
      await delay(CONNECTION_RETRY_DELAY_MS);
    }

    if (isMountedRef.current) {
      setOfflineNoticeVisible(false);
    }

    return true;
  }, [platform]);

  const applyAgeSignalDecision = useCallback(async (
    ageSignalResult: CheckAgeSignalsResult,
    context: {
      attempt: number;
      startedAt: number;
      source: string;
      runtimeEnvironment?: RuntimeEnvironmentResult | null;
      debugMode?: AgeGateStartupDebugMode;
      behavior?: string;
      uiMessageKey?: string;
      requiredRegulatoryFeatures?: RegulatoryFeature[];
      approvalPath?: string | null;
      approvalResult?: string | null;
      ignoreSignificantUpdateApprovalStatus?: boolean;
      rawPayload?: Record<string, unknown>;
    },
  ) => {
    const decision = getAgeSignalDecision(appName, ageSignalResult, {
      ignoreSignificantUpdateApprovalStatus: context.ignoreSignificantUpdateApprovalStatus,
    });
    console.log(`${logPrefix} age verification decision`, {
      allowed: decision.allowed,
      message: decision.message ?? null,
      platform,
      attempt: context.attempt,
      totalElapsedMs: Date.now() - context.startedAt,
      source: context.source,
      runtimeEnvironment: context.runtimeEnvironment ?? null,
      debugMode: context.debugMode ?? AgeGateStartupDebugMode.Auto,
      ageSignalResult,
    });

    if (!decision.allowed) {
      const blockedMessage = decision.message ?? `We're sorry but ${appName} is not available right now.`;
      trackTelemetryEvent({
        event_type: 'launch_result',
        allowed: false,
        behavior: context.behavior ?? 'block_age_signal_policy',
        ui_message_key: context.uiMessageKey ?? 'age_signal_policy_block',
        ui_message_text: blockedMessage,
        source: context.source,
        required_features: context.requiredRegulatoryFeatures ?? [],
        approval_path: context.approvalPath ?? null,
        approval_result: context.approvalResult ?? null,
        elapsed_ms: Date.now() - context.startedAt,
        ...getAgeAssuranceSignalFields(ageSignalResult),
        raw_payload: {
          ageSignalResult,
          runtimeEnvironment: context.runtimeEnvironment ?? null,
          debugMode: context.debugMode ?? AgeGateStartupDebugMode.Auto,
          ...context.rawPayload,
        },
      });
      console.warn(`${logPrefix} blocking app initialization`, {
        platform,
        attempt: context.attempt,
        source: context.source,
        blockedMessage,
        ageSignalResult,
      });
      onBlock(blockedMessage);
      return;
    }

    trackTelemetryEvent({
      event_type: 'launch_result',
      allowed: true,
      behavior: context.behavior ?? 'allow_age_signal',
      ui_message_key: null,
      ui_message_text: null,
      source: context.source,
      required_features: context.requiredRegulatoryFeatures ?? [],
      approval_path: context.approvalPath ?? null,
      approval_result: context.approvalResult ?? null,
      elapsed_ms: Date.now() - context.startedAt,
      ...getAgeAssuranceSignalFields(ageSignalResult),
      raw_payload: {
        ageSignalResult,
        runtimeEnvironment: context.runtimeEnvironment ?? null,
        debugMode: context.debugMode ?? AgeGateStartupDebugMode.Auto,
        ...context.rawPayload,
      },
    });

    console.log(`${logPrefix} allowing app initialization`, {
      platform,
      attempt: context.attempt,
      totalElapsedMs: Date.now() - context.startedAt,
      source: context.source,
      ageSignalResult,
    });
    await onAllow();
  }, [appName, onAllow, onBlock, platform, trackTelemetryEvent]);

  const runAgeCheck = useCallback(async () => {
    if (isMountedRef.current) {
      setIsProcessing(true);
      setErrorMessage('');
      setOfflineNoticeVisible(false);
    }

    const runAttempt = async (attempt: number): Promise<void> => {
      const startedAt = Date.now();
      let runtimeEnvironment: RuntimeEnvironmentResult | null = null;
      let startupDebugMode = AgeGateStartupDebugMode.Auto;

      try {
        await waitForNetworkConnection('before_age_verification_attempt');

        console.log(`${logPrefix} starting age verification`, {
          appName,
          platform,
          attempt,
          autoRun: isNativeAuto,
        });

        void flushQueuedAgeAssuranceTelemetry(uuid);

        try {
          runtimeEnvironment = await InsightAgeCheck.getRuntimeEnvironment();
          console.log(`${logPrefix} runtime environment`, {
            platform,
            attempt,
            runtimeEnvironment,
          });
        } catch (error) {
          console.warn(`${logPrefix} failed to load runtime environment; continuing with the live age-check flow`, {
            platform,
            attempt,
            error: toLoggableError(error),
          });
        }

        if (AGE_ASSURANCE_DEBUG_ENABLED) {
          try {
            startupDebugMode = await getStoredAgeGateStartupDebugMode();
            console.log(`${logPrefix} startup debug override`, {
              platform,
              attempt,
              startupDebugMode,
              honored: runtimeEnvironment?.isDebugBuild ?? false,
            });
          } catch (error) {
            console.warn(`${logPrefix} failed to load startup debug override`, {
              platform,
              attempt,
              error: toLoggableError(error),
            });
          }
        }

        if (
          AGE_ASSURANCE_DEBUG_ENABLED
          && runtimeEnvironment?.isDebugBuild
          && startupDebugMode !== AgeGateStartupDebugMode.Auto
        ) {
          if (startupDebugMode === AgeGateStartupDebugMode.BypassAllow) {
            console.log(`${logPrefix} debug startup override is bypassing age verification`, {
              platform,
              attempt,
              runtimeEnvironment,
              startupDebugMode,
            });
            trackTelemetryEvent({
              event_type: 'launch_result',
              allowed: true,
              behavior: 'allow_debug_bypass',
              ui_message_key: null,
              ui_message_text: null,
              source: 'debugStartupOverride',
              elapsed_ms: Date.now() - startedAt,
              raw_payload: {
                startupDebugMode,
                ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
              },
            });
            await onAllow();
            return;
          }

          const simulatedResult = getAgeGateStartupDebugResult(startupDebugMode);
          if (simulatedResult) {
            console.log(`${logPrefix} debug startup override is simulating an age signal result`, {
              platform,
              attempt,
              runtimeEnvironment,
              startupDebugMode,
              simulatedResult,
            });
            await applyAgeSignalDecision(simulatedResult, {
              attempt,
              startedAt,
              source: 'debugStartupOverride',
              runtimeEnvironment,
              debugMode: startupDebugMode,
              behavior: startupDebugMode === AgeGateStartupDebugMode.SimulateVerifiedAdult
                ? 'allow_debug_simulated_age_signal'
                : undefined,
              rawPayload: {
                startupDebugMode,
              },
            });
            return;
          }
        }

        if (runtimeEnvironment?.shouldAutoBypass) {
          console.log(`${logPrefix} runtime environment is auto-bypassing live age verification`, {
            platform,
            attempt,
            runtimeEnvironment,
            startupDebugMode,
          });
          trackTelemetryEvent({
            event_type: 'launch_result',
            allowed: true,
            behavior: 'allow_auto_bypass_unsupported_runtime',
            ui_message_key: null,
            ui_message_text: null,
            source: 'runtimeEnvironment',
            elapsed_ms: Date.now() - startedAt,
            raw_payload: {
              startupDebugMode,
              ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
            },
          });
          await onAllow();
          return;
        }

        let usedRegulatoryFeatures = false;
        let requiredRegulatoryFeatures: RegulatoryFeature[] = [];

        if (platform === 'ios') {
          try {
            const requiredFeaturesStartedAt = Date.now();
            const regulatoryFeatures = await InsightAgeCheck.checkRequiredRegulatoryFeatures();
            usedRegulatoryFeatures = true;
            requiredRegulatoryFeatures = regulatoryFeatures.requiredRegulatoryFeatures;

            console.log(`${logPrefix} Apple required regulatory features`, {
              platform,
              attempt,
              elapsedMs: Date.now() - requiredFeaturesStartedAt,
              regulatoryFeatures,
            });

            if (!requiredRegulatoryFeatures.length) {
              console.log(`${logPrefix} Apple did not require age verification on this device, continuing app load.`, {
                platform,
                attempt,
                totalElapsedMs: Date.now() - startedAt,
                checkSource: 'requiredRegulatoryFeatures',
              });
              trackTelemetryEvent({
                event_type: 'launch_result',
                allowed: true,
                behavior: 'allow_no_required_regulatory_features',
                ui_message_key: null,
                ui_message_text: null,
                source: 'requiredRegulatoryFeatures',
                required_features: [],
                elapsed_ms: Date.now() - startedAt,
                raw_payload: {
                  regulatoryFeatures,
                  ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                },
              });
              await onAllow();
              return;
            }
          } catch (error) {
            trackApiError('checkRequiredRegulatoryFeatures', error, {
              behavior: 'fallback_to_eligibility',
              elapsedMs: Date.now() - startedAt,
              source: 'requiredRegulatoryFeatures',
              extraRawPayload: getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
            });
            console.warn(`${logPrefix} requiredRegulatoryFeatures was unavailable or failed, falling back to eligibility`, {
              platform,
              attempt,
              error: toLoggableError(error),
            });
          }

          if (!usedRegulatoryFeatures) {
            const eligibilityStartedAt = Date.now();

            try {
              const eligibility = await InsightAgeCheck.checkEligibility();

              console.log(`${logPrefix} Apple eligibility result`, {
                platform,
                attempt,
                elapsedMs: Date.now() - eligibilityStartedAt,
                eligibility,
              });

              if (!eligibility.isEligible) {
                console.log(`${logPrefix} Apple did not require age verification on this device, continuing app load.`, {
                  platform,
                  attempt,
                  totalElapsedMs: Date.now() - startedAt,
                  checkSource: 'isEligibleForAgeFeatures',
                });
                trackTelemetryEvent({
                  event_type: 'launch_result',
                  allowed: true,
                  behavior: 'allow_not_eligible_for_age_features',
                  ui_message_key: null,
                  ui_message_text: null,
                  source: 'isEligibleForAgeFeatures',
                  required_features: requiredRegulatoryFeatures,
                  elapsed_ms: Date.now() - startedAt,
                  raw_payload: {
                    eligibility,
                    ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                  },
                });
                await onAllow();
                return;
              }
            } catch (error) {
              if (!isApiNotAvailableError(error)) {
                throw error;
              }

              console.log(`${logPrefix} Apple age assurance APIs are not available on this iOS version, continuing app load.`, {
                platform,
                attempt,
                totalElapsedMs: Date.now() - startedAt,
                checkSource: 'isEligibleForAgeFeatures',
                error: toLoggableError(error),
              });
              trackApiError('checkEligibility', error, {
                allowed: true,
                behavior: 'allow_ios_age_api_not_available',
                elapsedMs: Date.now() - startedAt,
                source: 'isEligibleForAgeFeatures',
                requiredRegulatoryFeatures,
                extraRawPayload: getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
              });
              trackTelemetryEvent({
                event_type: 'launch_result',
                allowed: true,
                behavior: 'allow_ios_age_api_not_available',
                ui_message_key: null,
                ui_message_text: null,
                source: 'isEligibleForAgeFeatures',
                required_features: requiredRegulatoryFeatures,
                elapsed_ms: Date.now() - startedAt,
                raw_payload: {
                  error: toLoggableError(error),
                  ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                },
              });
              await onAllow();
              return;
            }
          }
        }

        const ageSignalStartedAt = Date.now();
        const ageSignalResult: CheckAgeSignalsResult = await InsightAgeCheck.checkAgeSignals({
          ageGates: defaultAgeGates,
        });

        console.log(`${logPrefix} raw age signals result`, {
          platform,
          attempt,
          elapsedMs: Date.now() - ageSignalStartedAt,
          ageSignalResult,
        });

        let finalBehavior: string | undefined;
        let finalApprovalPath: string | null = null;
        let finalApprovalResult: string | null = null;
        let ignoreSignificantUpdateApprovalStatus = false;

        if (platform === 'ios') {
          const isCurrentIosSignificantUpdate = isIosSignificantUpdateEnabled();
          const iosSignificantUpdateDescription = getIosSignificantUpdateDescription(appName);
          ignoreSignificantUpdateApprovalStatus = !isCurrentIosSignificantUpdate;
          const requiresDeclaredAgeRange = usedRegulatoryFeatures
            ? hasRegulatoryFeature(requiredRegulatoryFeatures, RegulatoryFeature.DeclaredAgeRangeRequired)
            : true;
          const requiresAdultNotification = hasRegulatoryFeature(
            requiredRegulatoryFeatures,
            RegulatoryFeature.SignificantAppChangeRequiresAdultNotification,
          );
          const requiresParentalConsent = usedRegulatoryFeatures
            ? hasRegulatoryFeature(
              requiredRegulatoryFeatures,
              RegulatoryFeature.SignificantAppChangeRequiresParentalConsent,
            )
            : (
              ageSignalResult.userStatus === UserStatus.Supervised
              && hasParentalControl(
                ageSignalResult,
                ParentalControlType.SignificantAppChangeApprovalRequired,
              )
            );
          const ageInformationUnavailable = ageSignalResult.sharingDeclined
            || ageSignalResult.userStatus === UserStatus.Unknown
            || ageSignalResult.userStatus === UserStatus.Empty;

          console.log(`${logPrefix} iOS compliance inputs`, {
            platform,
            attempt,
            requiredRegulatoryFeatures,
            requiresDeclaredAgeRange,
            requiresAdultNotification,
            requiresParentalConsent,
            significantUpdateConfig: {
              id: iosSignificantUpdateConfig.id,
              isSignificantUpdate: iosSignificantUpdateConfig.isSignificantUpdate,
            },
            activeParentalControls: ageSignalResult.activeParentalControls ?? [],
            userStatus: ageSignalResult.userStatus,
            ageRangeDeclaration: ageSignalResult.ageRangeDeclaration ?? null,
            sharingDeclined: ageSignalResult.sharingDeclined ?? false,
          });

          if (requiresDeclaredAgeRange && ageInformationUnavailable) {
            const blockedMessage = getAgeInformationUnavailableMessage(appName);
            console.warn(`${logPrefix} blocking app initialization because Apple required declared age information but it was unavailable`, {
              platform,
              attempt,
              blockedMessage,
              requiredRegulatoryFeatures,
              ageSignalResult,
            });
            trackTelemetryEvent({
              event_type: 'launch_result',
              allowed: false,
              behavior: 'block_age_info_unavailable',
              ui_message_key: 'age_information_unavailable',
              ui_message_text: blockedMessage,
              source: 'iosCompliancePolicy',
              required_features: requiredRegulatoryFeatures,
              elapsed_ms: Date.now() - startedAt,
              ...getAgeAssuranceSignalFields(ageSignalResult),
              raw_payload: {
                ageSignalResult,
                requiredRegulatoryFeatures,
                ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
              },
            });
            onBlock(blockedMessage);
            return;
          }

          if (
            !isCurrentIosSignificantUpdate
            && (requiresParentalConsent || requiresAdultNotification)
          ) {
            console.log(`${logPrefix} iOS significant update flow skipped because this build is not marked as a significant update`, {
              platform,
              attempt,
              significantUpdateConfig: {
                id: iosSignificantUpdateConfig.id,
                isSignificantUpdate: iosSignificantUpdateConfig.isSignificantUpdate,
              },
              requiresParentalConsent,
              requiresAdultNotification,
              requiredRegulatoryFeatures,
            });
            finalBehavior = 'skip_significant_update_flow_not_current_update';
            finalApprovalPath = null;
            finalApprovalResult = null;
          } else if (
            !isCurrentIosSignificantUpdate
            && isSignificantUpdateApprovalStatus(ageSignalResult.userStatus)
          ) {
            console.log(`${logPrefix} iOS significant update approval status ignored because this build is not marked as a significant update`, {
              platform,
              attempt,
              userStatus: ageSignalResult.userStatus,
              significantUpdateConfig: {
                id: iosSignificantUpdateConfig.id,
                isSignificantUpdate: iosSignificantUpdateConfig.isSignificantUpdate,
              },
            });
            finalBehavior = 'allow_non_current_significant_update_status_ignored';
          } else if (requiresParentalConsent) {
            try {
              const approvalStartedAt = Date.now();
              const approvalResult = await InsightAgeCheck.requestSignificantUpdateApproval({
                description: iosSignificantUpdateDescription,
              });

              console.log(`${logPrefix} iOS significant update approval result`, {
                platform,
                attempt,
                elapsedMs: Date.now() - approvalStartedAt,
                approvalResult,
              });

              if (approvalResult.choice !== SignificantUpdateChoice.Approval) {
                const blockedMessage = getSignificantUpdateApprovalDeniedMessage(appName);
                console.warn(`${logPrefix} blocking app initialization because the significant update was not approved by a parent or guardian`, {
                  platform,
                  attempt,
                  blockedMessage,
                  approvalResult,
                  ageSignalResult,
                });
                trackTelemetryEvent({
                  event_type: 'launch_result',
                  allowed: false,
                  behavior: 'block_parent_denied',
                  ui_message_key: 'significant_update_approval_denied',
                  ui_message_text: blockedMessage,
                  source: 'requestSignificantUpdateApproval',
                  required_features: requiredRegulatoryFeatures,
                  approval_path: 'guardian_approval',
                  approval_result: approvalResult.choice,
                  elapsed_ms: Date.now() - startedAt,
                  ...getAgeAssuranceSignalFields(ageSignalResult),
                  raw_payload: {
                    ageSignalResult,
                    approvalResult,
                    requiredRegulatoryFeatures,
                    significantUpdateConfig: iosSignificantUpdateConfig,
                    ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                  },
                });
                onBlock(blockedMessage);
                return;
              }

              finalBehavior = 'allow_after_parent_approval';
              finalApprovalPath = 'guardian_approval';
              finalApprovalResult = approvalResult.choice;
            } catch (error) {
              const blockedMessage = getSignificantUpdateApprovalPendingMessage(appName);
              trackApiError('requestSignificantUpdateApproval', error, {
                allowed: false,
                behavior: 'block_parent_pending',
                elapsedMs: Date.now() - startedAt,
                source: 'requestSignificantUpdateApproval',
                ageSignalResult,
                requiredRegulatoryFeatures,
                extraRawPayload: {
                  ageSignalResult,
                  requiredRegulatoryFeatures,
                  significantUpdateConfig: iosSignificantUpdateConfig,
                  ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                },
              });
              console.warn(`${logPrefix} blocking app initialization because parental approval for the significant update could not be confirmed`, {
                platform,
                attempt,
                blockedMessage,
                error: toLoggableError(error),
                ageSignalResult,
                requiredRegulatoryFeatures,
              });
              trackTelemetryEvent({
                event_type: 'launch_result',
                allowed: false,
                behavior: 'block_parent_pending',
                ui_message_key: 'significant_update_approval_pending',
                ui_message_text: blockedMessage,
                source: 'requestSignificantUpdateApproval',
                required_features: requiredRegulatoryFeatures,
                approval_path: 'guardian_approval',
                approval_result: 'error',
                elapsed_ms: Date.now() - startedAt,
                ...getAgeAssuranceSignalFields(ageSignalResult),
                raw_payload: {
                  ageSignalResult,
                  requiredRegulatoryFeatures,
                  error: toLoggableError(error),
                  significantUpdateConfig: iosSignificantUpdateConfig,
                  ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                },
              });
              onBlock(blockedMessage);
              return;
            }
          } else if (requiresAdultNotification) {
            const alreadyAcknowledged = await hasStoredIosAdultAcknowledgment(appName);

            console.log(`${logPrefix} iOS adult significant update acknowledgment state`, {
              platform,
              attempt,
              alreadyAcknowledged,
            });

            if (!alreadyAcknowledged) {
              try {
                const acknowledgmentStartedAt = Date.now();
                const acknowledgmentResult = await InsightAgeCheck.showSignificantUpdateAcknowledgment({
                  description: iosSignificantUpdateDescription,
                });

                console.log(`${logPrefix} iOS significant update acknowledgment result`, {
                  platform,
                  attempt,
                  elapsedMs: Date.now() - acknowledgmentStartedAt,
                  acknowledgmentResult,
                });

                await setStoredIosAdultAcknowledgment(appName);
                finalBehavior = 'allow_after_adult_ack';
                finalApprovalPath = 'adult_acknowledgment';
                finalApprovalResult = 'acknowledged';
              } catch (error) {
                const blockedMessage = getAdultAcknowledgmentUnavailableMessage(appName);
                trackApiError('showSignificantUpdateAcknowledgment', error, {
                  allowed: false,
                  behavior: 'block_adult_ack_failed',
                  elapsedMs: Date.now() - startedAt,
                  source: 'showSignificantUpdateAcknowledgment',
                  ageSignalResult,
                  requiredRegulatoryFeatures,
                  extraRawPayload: {
                    ageSignalResult,
                    requiredRegulatoryFeatures,
                    significantUpdateConfig: iosSignificantUpdateConfig,
                    ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                  },
                });
                console.warn(`${logPrefix} blocking app initialization because the significant update could not be acknowledged`, {
                  platform,
                  attempt,
                  blockedMessage,
                  error: toLoggableError(error),
                  requiredRegulatoryFeatures,
                  ageSignalResult,
                });
                trackTelemetryEvent({
                  event_type: 'launch_result',
                  allowed: false,
                  behavior: 'block_adult_ack_failed',
                  ui_message_key: 'adult_acknowledgment_unavailable',
                  ui_message_text: blockedMessage,
                  source: 'showSignificantUpdateAcknowledgment',
                  required_features: requiredRegulatoryFeatures,
                  approval_path: 'adult_acknowledgment',
                  approval_result: 'error',
                  elapsed_ms: Date.now() - startedAt,
                  ...getAgeAssuranceSignalFields(ageSignalResult),
                  raw_payload: {
                    ageSignalResult,
                    requiredRegulatoryFeatures,
                    error: toLoggableError(error),
                    significantUpdateConfig: iosSignificantUpdateConfig,
                    ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
                  },
                });
                onBlock(blockedMessage);
                return;
              }
            } else {
              finalBehavior = 'allow_adult_ack_stored';
              finalApprovalPath = 'adult_acknowledgment';
              finalApprovalResult = 'stored';
            }
          }
        }

        if (!ageSignalResult.userStatus) {
          console.log(`${logPrefix} no explicit age signal returned; continuing with the normal app experience unless policy logic blocks it.`, {
            platform,
            attempt,
            ageSignalResult,
          });
        }

        await applyAgeSignalDecision(ageSignalResult, {
          attempt,
          startedAt,
          source: 'liveAgeSignals',
          runtimeEnvironment,
          debugMode: startupDebugMode,
          behavior: finalBehavior,
          requiredRegulatoryFeatures,
          approvalPath: finalApprovalPath,
          approvalResult: finalApprovalResult,
          ignoreSignificantUpdateApprovalStatus,
          rawPayload: platform === 'ios'
            ? {
              significantUpdateConfig: {
                id: iosSignificantUpdateConfig.id,
                isSignificantUpdate: iosSignificantUpdateConfig.isSignificantUpdate,
              },
            }
            : undefined,
        });
      } catch (error) {
        trackApiError('ageGateAttempt', error, {
          allowed: attempt === 1 ? null : false,
          behavior: attempt === 1 ? 'retry_after_age_gate_failure' : 'show_retry_error',
          elapsedMs: Date.now() - startedAt,
          source: 'ageGateAttempt',
          extraRawPayload: {
            startupDebugMode,
            ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
          },
        });
        console.error(`${logPrefix} age verification attempt failed`, {
          platform,
          attempt,
          totalElapsedMs: Date.now() - startedAt,
          runtimeEnvironment,
          startupDebugMode,
          error: toLoggableError(error),
        });

        if (!hasNetworkConnection()) {
          await waitForNetworkConnection('after_age_verification_network_failure');

          if (isMountedRef.current) {
            console.log(`${logPrefix} network connection restored; retrying age verification`, {
              platform,
              attempt,
            });
            await runAttempt(attempt);
          }

          return;
        }

        if (attempt === 1) {
          console.warn(`${logPrefix} retrying age verification after transient failure`, {
            platform,
            retryDelayMs: RETRY_DELAY_MS,
          });
          await delay(RETRY_DELAY_MS);
          await runAttempt(2);
          return;
        }

        if (isMountedRef.current) {
          const retryMessage = `${appName} could not confirm availability right now. Please try again.`;
          trackTelemetryEvent({
            event_type: 'launch_result',
            allowed: false,
            behavior: 'show_retry_error',
            ui_message_key: 'availability_check_failed',
            ui_message_text: retryMessage,
            source: 'ageGateAttempt',
            elapsed_ms: Date.now() - startedAt,
            raw_payload: {
              error: toLoggableError(error),
              startupDebugMode,
              ...getRuntimeEnvironmentTelemetryPayload(runtimeEnvironment),
            },
          });
          setErrorMessage(retryMessage);
        }
      }
    };

    try {
      await runAttempt(1);
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  }, [applyAgeSignalDecision, appName, isNativeAuto, onAllow, onBlock, platform, trackApiError, trackTelemetryEvent, uuid, waitForNetworkConnection]);

  useEffect(() => {
    if (!isNativeAuto || autoCheckStartedRef.current) {
      return;
    }

    autoCheckStartedRef.current = true;
    runAgeCheck().catch(() => {
      /** Error state is already surfaced in runAgeCheck. */
    });
  }, [isNativeAuto, runAgeCheck]);

  return (
    <IonPage>
      <IonContent className="launch-age-gate-content">
        <div className="launch-age-gate-shell">
          {!errorMessage ? (
            <div className="launch-age-gate-loading-shell">
              <div className="launch-age-gate-loading">
                <div className="launch-age-gate-loading-primary">
                  <IonSpinner name="crescent" />
                  <span>Preparing {appName}...</span>
                </div>
                {offlineNoticeVisible && (
                  <div className="launch-age-gate-offline-message">
                    {offlineConnectionMessage}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="launch-age-gate-card">
              <div className="launch-age-gate-kicker">{appName}</div>
              <div className="launch-age-gate-error">{errorMessage}</div>
              <IonButton
                className="launch-age-gate-button"
                disabled={isProcessing}
                onClick={() => runAgeCheck()}
              >
                Retry
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

export default LaunchAgeGate;
